<template>
  <form class="pp-billing" @submit.prevent="next">
    <p class="pp-billing__step">Passo {{ index + 1 }} de {{ steps.length }} · Dados para a nota fiscal</p>

    <template v-if="current === 'cpf'">
      <h3 class="pp-billing__title">Qual é o seu CPF?</h3>
      <p class="pp-billing__text">
        Vai para a nota fiscal da compra e fica salvo no seu cadastro da Recrutei.
      </p>
      <label class="pp-label" for="pp-billing-cpf">CPF</label>
      <input
        id="pp-billing-cpf"
        ref="cpf"
        v-model="form.cpf"
        class="pp-input"
        inputmode="numeric"
        autocomplete="off"
        placeholder="000.000.000-00"
        maxlength="14"
        @input="form.cpf = maskCpf(form.cpf)"
      />
    </template>

    <template v-else>
      <h3 class="pp-billing__title">Seu endereço</h3>
      <p class="pp-billing__text">
        Também vai para a nota. Fica salvo no seu cadastro, no mesmo lugar do perfil.
      </p>
      <div class="pp-billing__grid">
        <div class="pp-billing__field pp-billing__field--cep">
          <label class="pp-label" for="pp-billing-cep">CEP</label>
          <input
            id="pp-billing-cep"
            ref="cep"
            v-model="form.postalcode"
            class="pp-input"
            inputmode="numeric"
            autocomplete="postal-code"
            placeholder="00000-000"
            maxlength="9"
            @input="onCep"
          />
          <small v-if="cepStatus === 'loading'">Buscando o endereço...</small>
          <small v-else-if="cepStatus === 'error'">Não achamos esse CEP. Preencha abaixo.</small>
        </div>
        <div class="pp-billing__field pp-billing__field--wide">
          <label class="pp-label" for="pp-billing-street">Rua</label>
          <input id="pp-billing-street" v-model="form.address" class="pp-input" autocomplete="address-line1" maxlength="191" />
        </div>
        <div class="pp-billing__field">
          <label class="pp-label" for="pp-billing-number">Número</label>
          <input id="pp-billing-number" ref="number" v-model="form.address_number" class="pp-input" maxlength="20" />
        </div>
        <div class="pp-billing__field">
          <label class="pp-label" for="pp-billing-complement">Complemento <em>opcional</em></label>
          <input id="pp-billing-complement" v-model="form.address_complement" class="pp-input" autocomplete="address-line2" maxlength="100" />
        </div>
        <div class="pp-billing__field">
          <label class="pp-label" for="pp-billing-district">Bairro</label>
          <input id="pp-billing-district" v-model="form.address_neighborhood" class="pp-input" maxlength="100" />
        </div>
        <div class="pp-billing__field">
          <label class="pp-label" for="pp-billing-city">Cidade</label>
          <input id="pp-billing-city" v-model="form.city" class="pp-input" autocomplete="address-level2" maxlength="100" />
        </div>
        <div class="pp-billing__field pp-billing__field--uf">
          <label class="pp-label" for="pp-billing-uf">UF</label>
          <select id="pp-billing-uf" v-model="form.state" class="pp-input pp-billing__select">
            <option value="" disabled>--</option>
            <option v-for="uf in ufs" :key="uf" :value="uf">{{ uf }}</option>
          </select>
        </div>
      </div>
    </template>

    <p v-if="error" class="pp-billing__error">{{ error }}</p>

    <div class="pp-billing__actions">
      <button type="button" class="pp-btn" :disabled="saving" @click="back">Voltar</button>
      <button type="submit" class="pp-paywall__cta pp-billing__next" :disabled="saving">
        {{ saving ? "Salvando..." : isLast ? "Ir para o pagamento" : "Continuar" }}
      </button>
    </div>
  </form>
</template>

<script>
import service, { errorReason, errorStatus } from "@/services/publicProfile";
import mixin from "./mixin";

const UFS = [
  "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG", "PA",
  "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO",
];

// Mesma conta da API (BrazilianCpf): evita ida ao servidor só para dizer que o número está errado.
export function isValidCpf(value) {
  const cpf = String(value || "").replace(/\D/g, "");
  if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) return false;
  for (let t = 9; t < 11; t += 1) {
    let d = 0;
    for (let c = 0; c < t; c += 1) d += Number(cpf[c]) * (t + 1 - c);
    d = ((10 * d) % 11) % 10;
    if (Number(cpf[t]) !== d) return false;
  }
  return true;
}

const REQUIRED = {
  address: "a rua",
  address_number: "o número",
  address_neighborhood: "o bairro",
  city: "a cidade",
  state: "a UF",
};

/**
 * Antes do pagamento do PRO: pede só o que falta para a nota fiscal (CPF e endereço).
 * Tudo vai para o cadastro normal do talento, não para um lugar só de cobrança.
 */
export default {
  name: "BillingStep",
  mixins: [mixin],
  props: {
    info: { type: Object, required: true },
    // so para o analytics
    plan: { type: String, default: null },
    source: { type: String, default: "" },
  },
  data() {
    const address = this.info.address || {};
    return {
      index: 0,
      saving: false,
      error: "",
      cepStatus: "idle",
      ufs: UFS,
      form: {
        cpf: "",
        postalcode: address.postalcode || "",
        address: address.address || "",
        address_number: address.address_number || "",
        address_complement: address.address_complement || "",
        address_neighborhood: address.address_neighborhood || "",
        city: address.city || "",
        state: UFS.includes(String(address.state || "").toUpperCase()) ? String(address.state).toUpperCase() : "",
      },
    };
  },
  computed: {
    steps() {
      return this.info.has_cpf ? ["address"] : ["cpf", "address"];
    },
    current() {
      return this.steps[this.index];
    },
    isLast() {
      return this.index === this.steps.length - 1;
    },
  },
  watch: {
    current: {
      immediate: true,
      handler(step) {
        this.track("public_profile_billing_step_viewed", { step });
        this.$nextTick(() => {
          const el = step === "cpf" ? this.$refs.cpf : this.$refs.cep;
          if (el) el.focus();
        });
      },
    },
  },
  methods: {
    track(event, props = {}) {
      this.ppTrack(event, {
        plan: this.plan,
        source: this.source,
        has_cpf: !!this.info.has_cpf,
        steps: this.steps,
        step_index: this.index + 1,
        ...props,
      });
    },
    fail(error, step) {
      this.error = error.text;
      this.track("public_profile_billing_error", { step: step || this.current, error: error.code, message: error.text, ...(error.extra || {}) });
    },
    maskCpf(value) {
      const d = String(value || "").replace(/\D/g, "").slice(0, 11);
      return d
        .replace(/^(\d{3})(\d)/, "$1.$2")
        .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
        .replace(/\.(\d{3})(\d{1,2})$/, ".$1-$2");
    },
    async onCep() {
      const d = this.form.postalcode.replace(/\D/g, "").slice(0, 8);
      this.form.postalcode = d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
      if (d.length !== 8) {
        this.cepStatus = "idle";
        return;
      }
      this.cepStatus = "loading";
      try {
        const found = await this.$viaCep.buscarCep(this.form.postalcode);
        if (!found || found.erro) throw new Error("cep");
        this.form = {
          ...this.form,
          address: found.logradouro || this.form.address,
          address_neighborhood: found.bairro || this.form.address_neighborhood,
          city: found.localidade || this.form.city,
          state: found.uf || this.form.state,
        };
        this.cepStatus = "idle";
        this.track("public_profile_billing_cep_lookup", {
          result: "found",
          city: found.localidade || null,
          state: found.uf || null,
          filled_street: !!found.logradouro,
        });
        this.$nextTick(() => this.$refs.number && this.$refs.number.focus());
      } catch (e) {
        this.cepStatus = "error";
        this.track("public_profile_billing_cep_lookup", { result: "not_found" });
      }
    },
    back() {
      this.track("public_profile_billing_back_clicked", { step: this.current });
      this.error = "";
      if (this.index === 0) this.$emit("back");
      else this.index -= 1;
    },
    async next() {
      this.error = "";
      if (this.current === "cpf") {
        if (!isValidCpf(this.form.cpf)) {
          this.fail({ code: "cpf_invalid", text: "Esse CPF não é válido. Confira os números." });
          return;
        }
        this.track("public_profile_billing_step_completed", { step: "cpf" });
        this.index += 1;
        return;
      }
      if (this.form.postalcode.replace(/\D/g, "").length !== 8) {
        this.fail({ code: "cep_incomplete", text: "Preencha o CEP com 8 números." });
        return;
      }
      const missing = Object.keys(REQUIRED).filter((f) => !String(this.form[f] || "").trim());
      if (missing.length) {
        this.fail({
          code: "missing_fields",
          text: `Falta preencher ${missing.map((f) => REQUIRED[f]).join(", ")}.`,
          extra: { missing_fields: missing },
        });
        return;
      }
      this.saving = true;
      try {
        const payload = { ...this.form };
        if (this.info.has_cpf) delete payload.cpf;
        const info = await service.updateBillingInfo(payload);
        if (!info || !info.complete) throw new Error("incompleto");
        this.track("public_profile_billing_completed", {
          step: "address",
          city: this.form.city || null,
          state: this.form.state || null,
          has_complement: !!this.form.address_complement,
        });
        this.$emit("done");
      } catch (error) {
        const reason = errorReason(error);
        const status = errorStatus(error);
        if (reason === "cpf_taken" || reason === "cpf_invalid") {
          this.index = 0;
          this.fail(
            {
              code: reason,
              text:
                reason === "cpf_taken"
                  ? "Esse CPF já está em outra conta da Recrutei. Se for seu, fale com o suporte."
                  : "Esse CPF não é válido. Confira os números.",
              extra: { status, origin: "server" },
            },
            "cpf",
          );
        } else {
          this.fail({
            code: "save_failed",
            text: "Não conseguimos salvar agora. Tente de novo em instantes.",
            extra: { status, reason: reason || (error && error.message) || null },
          });
        }
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss">
.pp-billing {
  display: flex;
  flex-direction: column;
  font-family: Roboto, sans-serif;

  &__step {
    margin: 0 40px 0 0;
    color: #6554c0;
    font-size: 13px;
    font-weight: 500;
  }

  &__title {
    margin: 8px 40px 4px 0;
    color: #333;
    font-size: 22px;
    font-weight: 600;
  }

  &__text {
    margin: 0 0 20px;
    color: #696969;
    font-size: 14px;
    line-height: 1.4;
  }

  &__grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px 12px;
  }

  &__field {
    min-width: 0;

    small {
      display: block;
      margin-top: 4px;
      color: #696969;
      font-size: 12px;
    }

    em {
      color: #8a8a8a;
      font-size: 12px;
      font-style: normal;
      font-weight: 400;
    }

    &--wide {
      grid-column: 1 / -1;
    }
  }

  &__select {
    appearance: none;
    cursor: pointer;
  }

  &__error {
    margin: 14px 0 0;
    color: #c62828;
    font-size: 14px;
  }

  &__actions {
    display: flex;
    gap: 12px;
    margin-top: 24px;

    .pp-btn {
      height: 48px;
    }
  }

  &__next {
    flex: 1;
  }

  @media (max-width: 480px) {
    &__grid {
      grid-template-columns: 1fr;
    }
  }
}
</style>
